const DENSITY_RANK = { none: 0, light: 1, moderate: 2, dense: 3 };

const rank = (d) => DENSITY_RANK[d] || 0;
const isDense = (d) => d === 'dense';
const isAtLeast = (d, floor) => rank(d) >= rank(floor);

const WIN_PATH_LABEL = {
    combo: 'combo',
    combat: 'combat',
    alternate: 'alternate win',
    attrition: 'attrition',
};

function capitalize(s) {
    if (!s) return '';
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

export function deriveProfileMarkers(profile) {
    if (!profile) return [];

    const ramp = profile.ramp || {};
    const tutors = profile.tutors || {};
    const interaction = profile.interaction || {};
    const resourceDenial = profile.resourceDenial || {};
    const combos = profile.combos || {};
    const wincons = profile.wincons || {};
    const synergy = profile.synergy || {};
    const speed = profile.speed || {};
    const curve = profile.curve || {};
    const timing = profile.timing || {};
    const graveyard = profile.graveyard || {};
    const engine = profile.engine || {};
    const typal = profile.typal || {};
    const commanderReliance = profile.commanderReliance || {};

    const markers = [];
    const push = (m) => markers.push(m);
    const has = (key) => markers.some((m) => m.key === key);

    const controlShell = isDense(interaction.counterspells) && isAtLeast(interaction.removal, 'moderate');
    const prisonLock =
        isDense(resourceDenial.stax) &&
        (isAtLeast(resourceDenial.taxes, 'moderate') || isAtLeast(interaction.counterspells, 'moderate'));
    const voltron =
        (commanderReliance.equipmentAuraCount || 0) >= 8 &&
        Array.isArray(wincons.paths) &&
        wincons.paths.includes('combat');
    const bigMana = (curve.sixPlusCount || 0) >= 6 && isAtLeast(ramp.total, 'moderate');
    const fastAndTutored = !!speed.lowCurveDense && isDense(ramp.fastMana) && isDense(tutors.density);
    const flicker = (engine.etbTriggerCount || 0) >= 8 && (engine.flickerEnablerCount || 0) > 0;
    const reanimatorLean = (graveyard.reanimationCount || 0) > 0 && (graveyard.graveyardPayoffCount || 0) <= 3;

    if (controlShell) push({ key: 'controlShell', label: 'Control shell', tone: 'cool' });
    if (prisonLock) push({ key: 'prisonLock', label: 'Prison / lock', tone: 'cool' });
    if (isDense(graveyard.engagement)) push({ key: 'graveyardMatters', label: 'Graveyard matters', tone: 'warn' });
    if (reanimatorLean) push({ key: 'reanimator', label: 'Reanimator', tone: 'warn' });
    if (typal.isFocused && typal.dominantType) {
        push({ key: 'tribal', label: `${capitalize(typal.dominantType)} tribal`, tone: 'ok' });
    } else if (typal.dominantType && (typal.dominantTypeRatio || 0) > 0.3) {
        push({ key: 'tribalLean', label: `Leans ${capitalize(typal.dominantType)}`, tone: 'ok' });
    }
    if (voltron) push({ key: 'voltron', label: 'Voltron', tone: 'alert' });
    if (bigMana) push({ key: 'bigMana', label: 'Big mana', tone: 'warn' });
    if (flicker) push({ key: 'flicker', label: 'Flicker / blink', tone: 'warn' });
    if (fastAndTutored) push({ key: 'fastTutored', label: 'Fast & tutored', tone: 'alert' });

    if (Array.isArray(wincons.paths) && wincons.paths.length > 0) {
        const primary = wincons.primary || wincons.paths[0];
        const secondary = wincons.paths[1];
        const primaryLabel = WIN_PATH_LABEL[primary];
        if (primaryLabel) {
            const secondaryLabel = WIN_PATH_LABEL[secondary];
            const label = secondaryLabel
                ? `Wins through ${primaryLabel}, ${secondaryLabel} backup`
                : `Wins through ${primaryLabel}`;
            push({ key: 'winPath', label, tone: 'alert' });
        }
    }

    if (wincons.goal === 'broad') {
        push({ key: 'manyPlans', label: 'Many paths to win', tone: 'alert' });
    } else if (wincons.goal === 'narrow' && synergy.concentrated) {
        push({ key: 'focusedPlan', label: 'Focused single plan', tone: 'alert' });
    }

    if ((combos.effectiveLines || 0) >= 1 && combos.hasTwoCardLine) {
        push({ key: 'twoCardCombo', label: 'Two-card combo line', tone: 'alert' });
    }
    if (combos.isolatedFromWincon) {
        push({ key: 'valueCombos', label: 'Value combos', tone: 'warn' });
    }
    if (combos.enablerDense) {
        push({ key: 'comboInfra', label: 'Combo infrastructure, no finisher', tone: 'warn' });
    }

    if (synergy.concentrated && !has('focusedPlan')) {
        push({ key: 'focusedSynergy', label: 'Focused synergy build', tone: 'ok' });
    }

    if ((timing.flashEnablerCount || 0) > 0) {
        push({ key: 'flashEnablers', label: "Plays on opponents' turns", tone: 'warn' });
    } else if (isDense(timing.reactiveDensity) && !controlShell) {
        push({ key: 'reactive', label: 'Reactive shell', tone: 'cool' });
    }

    if (!fastAndTutored && !bigMana) {
        if (speed.lowCurveDense || curve.shape === 'bottom-heavy') {
            push({ key: 'lowCurve', label: 'Low curve', tone: 'alert' });
        } else if (curve.shape === 'top-heavy') {
            push({ key: 'highCurve', label: 'High curve', tone: 'warn' });
        }
    }

    if (commanderReliance.reliance === 'dense') {
        push({ key: 'builtAround', label: 'Built around commander', tone: 'ok' });
    } else if (commanderReliance.reliance === 'none') {
        push({ key: 'genericShell', label: 'Generic value shell', tone: 'warn' });
    }

    return markers;
}

const BOOST_LABELS = {
    executionTuning: 'Tuned for execution',
    narrowSynergyFocus: 'Cohesive synergy focus',
    synergyShellLift: 'Synergy shell lift',
    comboRedundancy: 'Redundant combo lines',
    comboInLightShell: 'Combos in a light shell',
    voltronFocus: 'Commander-focused equipment',
    fastManaPayoff: 'Fast mana package',
    manabaseQuality: 'Premium manabase',
    tutorManabaseOverlap: 'Tutors reinforce manabase',
    interactionDensity: 'Dense interaction',
    counterspellDensity: 'Heavy counterspells',
    disruptiveSynergy: 'Disruptive synergy',
    other: 'Other boosts',
};

const PENALTY_LABELS = {
    synergyWithoutClose: 'Synergy without a finisher',
    comboInconsistency: 'Combos lack support',
    winconInconsistency: 'Wincons hard to reach',
    incidentalCombos: 'Unsupported incidental combos',
    landsShellMismatch: 'Lands shell without execution',
    staxWithoutClose: 'Stax without a finisher',
    controlWithoutClose: 'Control without a finisher',
    enablerDenseWithoutClose: 'Enablers without a wincon',
    isolatedWinconPile: 'Disconnected win conditions',
    lowSynergyShell: 'Low-synergy pile',
    pacingMismatch: 'High curve, slow payoff',
    other: 'Other penalties',
};

const MAGNITUDE_RATIO = { minor: 0.33, notable: 0.67, major: 1 };
const MAGNITUDE_LABEL = { minor: 'Minor', notable: 'Notable', major: 'Major' };

// Keyed by the camelCase `id` values the API ships on each pattern entry — see
// api/src/shared/rules/processors/DeckProfileShape.js ANTI_PATTERN_LABELS.
// Used as a label fallback when an entry arrives without a pre-attached `label`.
const ANTI_PATTERN_LABELS = {
    lowFastMana: 'Low fast-mana for the score band',
    comboInconsistency: 'Combos without consistency',
    winconInconsistency: 'Wincons without consistency',
    incidentalCombos: 'Incidental precon combos',
    enablerDenseWithoutClose: 'Enabler-dense without a finisher',
    staxWithoutClose: 'Stax without a finisher',
    lowSynergyShell: 'Goodstuff shell, low synergy',
    dilutedComboPile: "Diluted combo pile (lines don't share infrastructure)",
    reactiveStallShell: 'Reactive shell without an engine',
    combatStrategy: 'Combat-heavy game plan',
    groupSlug: 'Group-slug pressure',
    pillowFort: 'Pillow-fort pieces without close',
    discardControl: 'Discard control without close',
    multiCardCombos: 'Multi-card combo reliance',
    lowQualityTutors: 'Low-quality tutor package',
    kitchenSinkWincon: 'Kitchen-sink wincon (bucket overlap)',
    winconWeakestLink: 'Wincon outpaces consistency/efficiency',
    casualStallShell: 'Casual stall shell — no clear win path',
    multiDeficitStack: 'Multi-deficit penalty stack (low fast-mana + low tutors)',
    structuralScoreFloor: 'Structural score floor',
    structuralScoreCeiling: 'Structural score ceiling',
    incompleteDeckData: 'Incomplete deck data (some cards not recognized)',
};

const INFO_VARIANT_ANTI_PATTERNS = new Set(['incompleteDeckData']);

function humanizeSnakeCase(id) {
    if (!id) return '';
    const spaced = String(id).replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function normalizeAntiPatternEntry(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const id = raw.id || raw.type;
    if (!id) return null;
    const severity = raw.severity || 'minor';
    const label = raw.label || ANTI_PATTERN_LABELS[id] || humanizeSnakeCase(id);
    const variant = INFO_VARIANT_ANTI_PATTERNS.has(id) ? 'info' : 'warning';
    return {
        id,
        label,
        severity,
        severityLabel: MAGNITUDE_LABEL[severity] || '',
        why: raw.why || '',
        variant,
    };
}

const NET_DIRECTION_COPY = {
    up: {
        minor: 'Adjustments nudged the score up slightly.',
        notable: 'Adjustments lifted the score above its base.',
        major: 'Adjustments pushed the score well above its base.',
    },
    down: {
        minor: 'Adjustments nudged the score down slightly.',
        notable: 'Adjustments pulled the score below its base.',
        major: 'Adjustments pulled the score well below its base.',
    },
    flat: {
        none: 'Boosts and penalties roughly cancelled out.',
        minor: 'Boosts and penalties roughly cancelled out.',
    },
};

function buildEntries(bag, labels, caps) {
    return Object.entries(bag || {})
        .filter(([, magnitude]) => magnitude && magnitude !== 'none')
        .map(([key, magnitude]) => ({
            key,
            label: labels[key] || labels.other,
            magnitude,
            magnitudeLabel: MAGNITUDE_LABEL[magnitude] || '',
            ratio: MAGNITUDE_RATIO[magnitude] || 0,
            capped: !!(caps && caps[key]),
        }))
        .sort((a, b) => b.ratio - a.ratio);
}

export function deriveProfileScoreDrivers(profile) {
    const adjustments = profile?.scoreAdjustments;
    if (!adjustments) return null;

    const boosts = buildEntries(adjustments.boosts, BOOST_LABELS, null);
    const penalties = buildEntries(adjustments.penalties, PENALTY_LABELS, adjustments.caps);

    const netDirection = adjustments.netDirection || 'flat';
    const netMagnitude = adjustments.netMagnitude || 'none';
    const netCopyByMagnitude = NET_DIRECTION_COPY[netDirection] || NET_DIRECTION_COPY.flat;
    const netCopy = netCopyByMagnitude[netMagnitude] || netCopyByMagnitude.minor || '';

    const antiPattern = adjustments.antiPattern || null;
    const antiPatternActive = !!(antiPattern && antiPattern.active);

    const allPatterns = antiPatternActive
        ? (Array.isArray(antiPattern.patterns) ? antiPattern.patterns : [])
              .map(normalizeAntiPatternEntry)
              .filter(Boolean)
        : [];

    const warningPatterns = allPatterns.filter((p) => p.variant !== 'info');
    const dataIssues = allPatterns.filter((p) => p.variant === 'info');
    const hasWarningPatterns = warningPatterns.length > 0;
    const hasDataIssues = dataIssues.length > 0;

    const warningAntiPattern = hasWarningPatterns
        ? {
              ...antiPattern,
              patterns: warningPatterns,
              patternCount: warningPatterns.length,
          }
        : null;

    return {
        boosts,
        penalties,
        netDirection,
        netMagnitude,
        netMagnitudeLabel: MAGNITUDE_LABEL[netMagnitude] || '',
        netCopy,
        antiPattern: warningAntiPattern,
        antiPatternSeverityLabel: warningAntiPattern ? MAGNITUDE_LABEL[antiPattern.severity] || '' : '',
        dataIssues,
        hasAnyContent: boosts.length > 0 || penalties.length > 0 || hasWarningPatterns || hasDataIssues,
    };
}

// ---- Manabase profile score-driver adapter ---------------------------
// The manabase profile uses the same `scoreDrivers` shape as the
// power-level profile's `scoreAdjustments` (boosts, penalties, netDirection,
// netMagnitude) but with different keys and an orthogonal `caps` object of
// booleans describing which cap-style guards fired. Returning the same
// shape PowerLevelPane consumes lets us reuse `ScoreDriverRow` verbatim.

const MANABASE_BOOST_LABELS = {
    premiumFetches: 'Premium fetches',
    originalDuals: 'Original duals',
    utilityLands: 'Utility lands',
    speedLands: 'Speed lands',
    mdfcFlexibility: 'MDFC flexibility',
    commanderAcceleration: 'Commander acceleration',
    other: 'Other boosts',
};

const MANABASE_PENALTY_LABELS = {
    tapLands: 'Tap lands',
    manaDeficiency: 'Mana deficiency',
    noFastManaAtHighQuality: 'No fast mana at high quality',
    other: 'Other penalties',
};

const MANABASE_CAP_LABELS = {
    manyTapLandsCap: 'Many tap-lands cap applied',
    manyTapLandsSevereCap: 'Severe tap-land cap applied',
    noUtilitySpeedCap: 'No utility / speed cap applied',
    qualityCeiling: 'Quality ceiling reached',
};

export function deriveManabaseScoreDrivers(manabaseProfile) {
    const drivers = manabaseProfile?.scoreDrivers;
    if (!drivers) return null;

    const boosts = buildEntries(drivers.boosts, MANABASE_BOOST_LABELS, null);
    const penalties = buildEntries(drivers.penalties, MANABASE_PENALTY_LABELS, null);

    const caps = Object.entries(drivers.caps || {})
        .filter(([, active]) => !!active)
        .map(([key]) => ({ key, label: MANABASE_CAP_LABELS[key] || key }));

    const netDirection = drivers.netDirection || 'flat';
    const netMagnitude = drivers.netMagnitude || 'none';
    const netCopyByMagnitude = NET_DIRECTION_COPY[netDirection] || NET_DIRECTION_COPY.flat;
    const netCopy = netCopyByMagnitude[netMagnitude] || netCopyByMagnitude.minor || '';

    return {
        boosts,
        penalties,
        caps,
        netDirection,
        netMagnitude,
        netMagnitudeLabel: MAGNITUDE_LABEL[netMagnitude] || '',
        netCopy,
        hasAnyContent: boosts.length > 0 || penalties.length > 0 || caps.length > 0,
    };
}
